import colorPallete, { labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainer, GridItem } from "@insite/mobius/index";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React from 'react';
import styled, { css, keyframes } from 'styled-components';

const shimmerProductSearchImage = keyframes`
  0% {
    background-color: ${colorPallete.grey400};
  }
  25% {
    background-color: ${colorPallete.grey200};
  }
  50% {
    background-color: ${colorPallete.grey400};
  }
  75% {
    background-color: ${colorPallete.grey200};
  }
  100% {
    background-color: ${colorPallete.grey100};
  }
`;

const shimmerProductSearchText = keyframes`
  0% {
    background-color: ${colorPallete.grey200};
  }
  25% {
    background-color: ${colorPallete.grey400};
  }
  50% {
    background-color: ${colorPallete.grey200};
  }
  75% {
    background-color: ${colorPallete.grey400};
  }
  100% {
    background-color: ${colorPallete.grey100};
  }
`;

interface SkeletonDivProps {
  width?: string;
  height?: string;
}

const SkeletonDiv = styled.div<SkeletonDivProps>`
`;

interface ProductSearchSkeletonDivProps {
  width?: string;
  height?: string;
  animationKeyframe?: ReturnType<typeof keyframes>;
}


const ProductSearchSkeletonDiv = styled.div<ProductSearchSkeletonDivProps>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  animation: ${props => props.animationKeyframe || shimmerProductSearchText} 1350ms infinite;
`;

const ProductSearchImagePlaceholder = styled(ProductSearchSkeletonDiv).attrs(() => ({
  animationKeyframe: shimmerProductSearchImage,
}))`
  width: 80px;
  height: 80px;
  margin-right: 24px;
  border-radius: 15px;
`;


interface SkeletonLoaderProps {
  width?: string;
  height?: string;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ width, height }) => {
  return <SkeletonDiv width={width} height={height} />;
};

export default SkeletonLoader;

const FacetContainer = styled.div`
`;

const Facet = styled.div`
`;

const FacetList = styled.ul`
`;

const FacetItem = styled.li`
`;

const CheckboxSkeleton = styled(SkeletonDiv)`
`;

interface FacetSkeletonLoaderProps {
  numberOfFacets?: number;
  numberOfOptions?: number;
}

export const FacetSkeletonLoader: React.FC<FacetSkeletonLoaderProps> = ({
  numberOfFacets = 1,
  numberOfOptions = 5,
}) => {
  return (
    <FacetContainer>
      {[...Array(numberOfFacets)].map((_, i) => (
        <Facet key={i}>
          <SkeletonLoader width="" height="" />
          <FacetList>
            {[...Array(numberOfOptions)].map((_, j) => (
              <FacetItem key={j}>
                <CheckboxSkeleton />
                <SkeletonLoader width="" height="" />
              </FacetItem>
            ))}
          </FacetList>
        </Facet>
      ))}
    </FacetContainer>
  );
};

export const SearchInput = styled.div`
  margin: 10px 0;
  input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
`;

export const SearchResults = styled.div`
  margin: 10px 0;
`;

export const SearchResultItem = styled.div`
  padding: 8px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
  mark {
    background-color: #ffeb3b;
    padding: 0 2px;
  }
`;

const ProductSearchContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  flex-direction: column;
  font: ${labelFonts.label2Regular.fontWeight} ${labelFonts.label2Regular.fontSize}/${labelFonts.label2Regular.lineHeight} ${labelFonts.label2Regular.fontFamily};
  color: ${colorPallete.grey600};
  margin-top: 8px;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      null,
      css`
          padding: 0 9px;
      `,
  ], "min")}   
`;

const ProductSearch = styled.div`
  display: flex;
  align-items: center;
  border-radius: 8px;
  border: 1px solid ${colorPallete.grey300};
  padding: 7px 16px;
`;

const ProductSearchInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ProductSearchPartsSkeletonWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  color: ${colorPallete.grey600};
`;

const ProductSearchTitleSkeleton = styled(ProductSearchSkeletonDiv)`
  height: 24px;
  border-radius: 4px;
  margin-bottom: 4px;
`;

const ProductSearchBrandSkeleton = styled(ProductSearchSkeletonDiv)`
  height: 17px;
  border-radius: 4px;
  margin-bottom: 4px;
  max-width: 192px;
  width: 100%;
`;

const ProductSearchPartsSkeleton = styled(ProductSearchSkeletonDiv)`
  height: 17px;
  max-width: 52px;
  width: 100%;
`;

interface ProductSkeletonLoaderProps {
  numberOfSuggestions?: number;
}

export const ProductSkeletonLoader: React.FC<ProductSkeletonLoaderProps> = ({
  numberOfSuggestions = 1,
}) => {
  return (
    <ProductSearchContainer>
      {[...Array(numberOfSuggestions)].map((_, index) => (
        <ProductSearch key={index}>
          <ProductSearchImagePlaceholder />
          <ProductSearchInfo>
            <ProductSearchBrandSkeleton/>
            <ProductSearchTitleSkeleton />
            <ProductSearchPartsSkeletonWrapper>
              {translate("MFG Part#")} <ProductSearchPartsSkeleton />
              {translate("DSG#")} <ProductSearchPartsSkeleton />
            </ProductSearchPartsSkeletonWrapper>
           
          </ProductSearchInfo>
        </ProductSearch>
      ))}
    </ProductSearchContainer>
  );
};

//Product List Page Skeleton
const shimmerProductList = keyframes`
  0% {
    left: -300px;
  }
  100% {
    left: 100%;
  }
`;

interface ProductListSkeletonLoaderProps {
  numberOfProductSuggestions?: number;
}

interface ProductListSkeletonDivProps {
  width?: string;
  height?: string;
  animationKeyframe?: ReturnType<typeof keyframes>;
}

const ProductListSkeletonDiv = styled.div<ProductListSkeletonDivProps>`
  position: relative;
  overflow: hidden;
  background: ${colorPallete.grey200}; 
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  border-radius: 36px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -320px;
    height: 100%;
    width: 240px;
    background: linear-gradient(270deg, rgba(246, 246, 247, 0) 0%, #909091 48%, rgba(197, 197, 198, 0) 100%);
    animation: ${props => props.animationKeyframe || shimmerProductList} 1.5s infinite;
  }
`;

const ProductListTitle = styled(ProductListSkeletonDiv)`
  width: 100%;
  height: 48px;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      null,
      css`
        max-width: 320px;
      `,
  ], "min")}   
`;

const ProductListFilters = styled(ProductListSkeletonDiv)`
  width: 100%;
  height: 48px;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      null,
      css`
        width: 275px;
        height: 620px;
      `,
  ], "min")}   
`;

const ProductListCardImage = styled(ProductListSkeletonDiv)`
  width: 177px;
  height: 190px;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      css`
        display: none;
      `,
  ], "min")}   
`;

const ProductListCardButton = styled(ProductListSkeletonDiv)`
  width: 100%;
  height: 39px;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      css`
        display: none;
      `,
  ], "min")}   
`;

const ProductListCardInfo = styled(ProductListSkeletonDiv)`
  width: 100%;
  height: 400px;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      css`
        height: 295px;
      `,
  ], "min")}   
`;

const ProductListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -4px;
  padding: 5px 0;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      css`
        margin: 0;
      `,
      css`
        padding: 68px 14px 63px;
      `,
  ], "min")}   
`;
const ProductListTopContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;
const ProductListLeftContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      null,
      css`
        width: 275px;
        margin-bottom: 0;
      `,
  ], "min")}   
`;
const ProductListRightContainer = styled.div`
  width: 100%;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      null,
      css`
        width: calc(100% - 275px);
        padding-left: 36px;
      `,
  ], "min")}   
`;
const ProductListProductItemContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 41px 0;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      css`
        gap: 30px 42px;
      `,
  ], "min")}   
`;
const ProductListProductItemColumn = styled.div`
  width: 100%;
  display: flex;
  gap: 13px 0;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      css`
        width: calc(100%/3 - 28px);
        height: 295px;
      `,
  ], "min")}  
`;


const ProductListCardInfoWrapper = styled.div`
  width: calc(100% - 177px);
  height: 452px;
  display: flex;
  flex-direction: column;
  padding-left: 13px;
  gap: 13px 0;
  ${({ theme }: { theme: BaseTheme }) =>
  breakpointMediaQueries(theme, [
      null,
      null,
      css`
        width: 100%;
        height: auto;
        padding-left: 0;
      `,
  ], "min")}  
`;

export const ProductListSkeletonLoader: React.FC<ProductListSkeletonLoaderProps> = ({
  numberOfProductSuggestions = 6,
}) => {
  return (
    <ProductListContainer>
      <ProductListTopContainer>
        <ProductListTitle />
      </ProductListTopContainer>
      <ProductListLeftContainer>
        <ProductListFilters />
      </ProductListLeftContainer>
      <ProductListRightContainer>
        <ProductListProductItemContainer>
          {[...Array(numberOfProductSuggestions)].map((_, index) => (
            <ProductListProductItemColumn key={index}>
              <ProductListCardImage/>
              <ProductListCardInfoWrapper>
                <ProductListCardInfo />
                <ProductListCardButton/>
              </ProductListCardInfoWrapper>
            </ProductListProductItemColumn>
          ))}
        </ProductListProductItemContainer>
      </ProductListRightContainer>
    </ProductListContainer>
  );
};